import { useState, useCallback } from "react";
import axios from "axios";
import type {
  ClientStepperData,
  TravelDetailsData,
  PlanSelection,
  GroupTransactionRequest,
  GroupTransactionPrincipal,
  GroupMember,
  UtmTrackingData,
} from "../types/customer-portal";
import { enhancedApi } from "@/shared/services/enhancedApi";
import { usePlanSelection } from "../contexts/PlanSelectionContext";
import { STORAGE_KEYS } from "../utils/sessionStateManager";
import { useAppStore } from "@/store/useAppStore";
import { buildUtmQsParams } from "@/shared/utils/utmUtils";
import { CoverageType } from "@/shared/types/applicationTripTypes";

// Plan option interface from PlanSelectionContext
interface PlanOption {
  productId: number;
  productName: string;
  productLabel: string;
  maxBenefitLimit: number;
  planType: string;
  rate: {
    rateAmount: number;
    currency: {
      currencyId: number;
      currencyCode: string;
      symbol: string;
    };
  };
  benefitsCategories: Array<{
    benefitsCategory: string;
    categoryCoverage: string;
    benefits: Array<{
      benefitName: string;
      benefitCoverage: string;
    }>;
  }>;
  exclusion: string[];
}

// Contact information interface
interface ContactInfo {
  landlineCd: string;
  landlineAc: string;
  landLineNumber: string;
  contactCd?: string;
  contactNumber?: string;
  email: string;
  phoneNumber?: string;
  countryCode?: string;
}

// Address information interface
interface AddressInfo {
  address1: string;
  barangay: string;
  city: string;
  province: string;
  zipCode: string;
  country: string;
}

// Principal information interface
interface PrincipalInfo {
  principal_id: number;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  birthDate: string;
  gender: string;
  occupation: string;
  nationality: string;
  civilStatus: string;
  idType: string;
  idNumber: string;
  address: AddressInfo;
  contact: ContactInfo;
}

// Transaction details interface
interface TransactionDetails {
  numberOfTraveler: number;
  travelPurpose: string;
  origin: string;
  destinations: string;
  itinerary: string;
  coverageStart: string;
  coverageEnd: string;
  endTrip: string;
}

// Multi-Trip specific transaction details interface (simplified structure)
interface MultiTripTransactionDetails {
  numberOfTraveler: number;
  travelPurpose: string;
  coverageStart: string; // Only coverageStart, no coverageEnd for multi-trip
}

// Product interface
interface ProductInfo {
  productId: number;
}

// Traveler details interface
interface TravelerDetails {
  travelerTypeId: number;
}

// Applicant interface
interface ApplicantInfo {
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
}

// Beneficiary interface
interface BeneficiaryInfo {
  title: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix: string;
  relationship: string;
  address?: AddressInfo; // Optional for family transactions
}

// Family member interface for family transactions
interface FamilyMemberInfo {
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  birthDate: string;
  gender: string;
  relationship: string;
  idType: string;
  idNumber: string;
  occupation: string;
  nationality: string;
  address: {
    isSameAsPrincipal: boolean;
    address1?: string;
    barangay?: string;
    city?: string;
    province?: string;
    zipCode?: string;
    country?: string;
  };
}

// Payment interface
interface PaymentInfo {
  currencyId: number;
  paymentAmount: number;
  paymentTotalAmount: number;
}

// ShortSecure interface (only for ShortSecure plans)
interface ShortSecureInfo {
  transportMode: string;
}

// Plan type for determining transaction endpoint
type PlanType = "Individual" | "Family" | "Group" | "Multi Trip";

// Base transaction request interface
interface BaseTransactionRequest {
  dataId: string;
  isForSomeoneElse: boolean;
  principal: PrincipalInfo;
  transactionDetails: TransactionDetails;
  product: ProductInfo;
  travelerDetails: TravelerDetails;
  applicant: ApplicantInfo;
  beneficiaries: BeneficiaryInfo[];
  payment: PaymentInfo;
  shortSecure?: ShortSecureInfo; // Optional, only for ShortSecure plans
}

// Individual transaction request type (same as base)
type IndividualTransactionRequest = BaseTransactionRequest;

// Family transaction request interface (extends base with family members)
interface FamilyTransactionRequest extends BaseTransactionRequest {
  family: FamilyMemberInfo[];
}

// Multi-Trip transaction request interface (matches sample request structure)
interface MultiTripTransactionRequest {
  dataId: string;
  principal: PrincipalInfo;
  transactionDetails: MultiTripTransactionDetails;
  product: ProductInfo;
  travelerDetails: TravelerDetails; // Added travelerDetails for Multi Trip
  applicant: ApplicantInfo;
  beneficiaries: BeneficiaryInfo[];
  payment: PaymentInfo;
}

// Union type for all transaction requests
type TransactionRequest =
  | IndividualTransactionRequest
  | FamilyTransactionRequest
  | MultiTripTransactionRequest;

// Transaction response interface
interface TransactionResponse {
  success: boolean;
  code: number;
  paymentLink: string;
  message: string;
  hasPendingError?: boolean;
  hasOverlappingError?: boolean;
  transactionDetails?: {
    payment_link?: string;
    coverage_start?: string;
    coverage_end?: string;
    itinerary?: string;
    expiry_date?: string;
  };
}

// Temp data interface for fallback values
interface TempData {
  [key: string]: unknown;
  productName?: string;
  typeName?: string;
  rateAmount?: number;
  currencyCode?: string;
  currencyId?: number;
  startTrip?: string;
  destinations?: string | string[];
  endTrip?: string;
  startDate?: string;
  endDate?: string;
  numberOfTraveler?: number;
  numberOfTravelers?: number;
  travelPurpose?: string;
  purposeOfTravel?: string;
  principal_id?: number;
  principalId?: number;
  middleName?: string;
  suffix?: string;
  birthDate?: string;
  dateOfBirth?: string;
  email?: string;
  mbl?: string | number;
}

// Enhanced plan data interface (from SummaryStep)
interface EnhancedPlanData {
  planName: string;
  productType: string;
  typeOfPlan: string;
  plan: string;
  medicalTreatmentLimit: string;
  originalPremium: string;
  paymentPremium: string;
}

// Hook return interface
interface UseTransactionReturn {
  createTransaction: (
    data: ClientStepperData,
    selectedPlan: PlanOption | null,
    travelDetailsData?: TravelDetailsData,
    planSelection?: PlanSelection,
    tempData?: TempData | null,
    enhancedPlanData?: EnhancedPlanData | null,
    contextGetPrincipalId?: () => number | null,
    utmData?: UtmTrackingData | null,
  ) => Promise<TransactionResponse>;
  loading: boolean;
  error: string | null;
  success: boolean;
  paymentLink: string | null;
  clearState: () => void;
}



export const useTransaction = (): UseTransactionReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const planSelectionTempData = localStorage.getItem("planSelectionTempData");
  const customerPortalSessionState = sessionStorage.getItem(
    STORAGE_KEYS.SESSION_STATE,
  );
  const { tempDataId, coverageType } = useAppStore();

  // Access principal data from context for email verification data
  const { principalData } = usePlanSelection();
  // Helper function to get API endpoint based on plan type
  const getTransactionEndpoint = useCallback((planType: PlanType, utmData?: UtmTrackingData | null): string => {
    const utmQsParams = buildUtmQsParams(utmData);

    switch (planType) {
      case "Individual":
        return "/api/transactions/individual" + utmQsParams;
      case "Family":
        return "/api/transactions/family" + utmQsParams;
      case "Group":
        return "/api/transactions/group" + utmQsParams;
      case "Multi Trip":
        return "/api/transactions/multitrip" + utmQsParams;
      default:
        return "/api/transactions/individual" + utmQsParams;
    }
  }, []);

  // Helper function to determine plan type from data
  const determinePlanType = useCallback(
    (
      data: ClientStepperData,
      planSelection?: PlanSelection,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _?: TravelDetailsData,
    ): PlanType => {
      // TODO: remove because unused var
      if (customerPortalSessionState) {
        const parsedCustomerPortalSessionState = JSON.parse(
          customerPortalSessionState,
        );
        const planTypeToCheck =
          parsedCustomerPortalSessionState?.travelFormData?.planType ||
          "Individual";
        return planTypeToCheck;
      }

      // Second priority: plan selection
      if (planSelection?.planType) {
        const planTypeMap: Record<string, PlanType> = {
          "Individual Plan": "Individual",
          "Family Plan": "Family",
          "Group Plan": "Group",
          "Multi Trip": "Multi Trip",
        };
        return planTypeMap[planSelection.planType] || "Individual";
      }

      // Third priority: infer from data structure
      const groupMembers = data.groupinformation?.groupMembers || [];
      const spouse = data.familyinformation?.spouse;
      const children = data.familyinformation?.children || [];

      if (groupMembers.length > 0) {
        return "Group";
      } else if (spouse || children.length > 0) {
        return "Family";
      }

      return "Individual";
    },
    [],
  );

  // Helper function to get traveler type ID based on plan type
  const getTravelerTypeId = useCallback((planType: PlanType): number => {
    switch (planType) {
      case "Individual":
        return 1;
      case "Family":
        return 2;
      case "Group":
        return 3;
      case "Multi Trip":
        return 4; // Multi Trip has its own unique traveler type ID
      default:
        return 1;
    }
  }, []);

  // Helper function to format phone number for contact
  const formatPhoneNumber = useCallback(
    (phoneNumber?: string): { contactCd: string; contactNumber: string } => {
      if (!phoneNumber) {
        return { contactCd: "63", contactNumber: "" }; // Empty when not provided
      }

      // Remove any non-digit characters
      const cleanNumber = phoneNumber.replace(/\D/g, "");

      // If it starts with 63, split it
      if (cleanNumber.startsWith("63")) {
        return {
          contactCd: "63",
          contactNumber: cleanNumber.substring(2),
        };
      }

      // If it starts with 0, remove it and add 63
      if (cleanNumber.startsWith("0")) {
        return {
          contactCd: "63",
          contactNumber: cleanNumber.substring(1),
        };
      }

      // Default case
      return {
        contactCd: "63",
        contactNumber: cleanNumber || "",
      };
    },
    [],
  );

  // Helper function to get email from contact information
  const getEmailAddress = useCallback(
    (data: ClientStepperData, tempData?: TempData | null): string => {
      // Try to get email from various sources
      if (data.contactinformation && "email" in data.contactinformation) {
        return data.contactinformation.email as string;
      }

      if (
        tempData &&
        "email" in tempData &&
        typeof tempData.email === "string"
      ) {
        return tempData.email;
      }

      // Return empty string when no email is provided
      return "";
    },
    [],
  );

  // Helper function to calculate number of travelers with tempData integration
  const getNumberOfTravelers = useCallback(
    (
      data: ClientStepperData,
      planType: PlanType,
      tempData?: TempData | null,
    ): number => {
      // First try to get from tempData
      if (
        tempData &&
        "numberOfTraveler" in tempData &&
        typeof tempData.numberOfTraveler === "number" &&
        tempData.numberOfTraveler > 0
      ) {
        return tempData.numberOfTraveler;
      }
      if (
        tempData &&
        "numberOfTravelers" in tempData &&
        typeof tempData.numberOfTravelers === "number" &&
        tempData.numberOfTravelers > 0
      ) {
        return tempData.numberOfTravelers;
      }

      // Fallback to calculating from data structure
      if (planType === "Group") {
        const count = data.groupinformation?.groupMembers?.length || 1;
        return count;
      }

      if (planType === "Family") {
        let count = 1; // Principal insured
        if (data.familyinformation?.spouse) count++;
        if (data.familyinformation?.children)
          count += data.familyinformation.children.length;
        return count;
      }

      return 1; // Individual
    },
    [],
  );

  // Helper function to calculate days between two dates
  const calculateTripDays = useCallback(
    (startDate: string, endDate: string): number => {
      try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const timeDifference = end.getTime() - start.getTime();
        const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
        return Math.max(1, daysDifference); // Ensure at least 1 day
      } catch (error) {
        console.warn("Error calculating trip days:", error);
        return 5; // Default to 5 days
      }
    },
    [],
  );

  // Helper function to get travel information with comprehensive tempData fallback logic
  const getTravelInformation = useCallback(
    (travelDetailsData?: TravelDetailsData, tempData?: TempData | null) => {
      // Helper function to get tempData travel details
      const getTempDataTravelDetails = () => {
        if (!tempData || typeof tempData !== "object") return null;

        const startTrip =
          "startTrip" in tempData &&
          typeof tempData.startTrip === "string" &&
          tempData.startTrip.trim() !== ""
            ? tempData.startTrip
            : null;

        // Handle destinations as either string or array
        let destinations = null;
        if ("destinations" in tempData) {
          if (
            typeof tempData.destinations === "string" &&
            tempData.destinations.trim() !== ""
          ) {
            destinations = tempData.destinations;
          } else if (
            Array.isArray(tempData.destinations) &&
            tempData.destinations.length > 0
          ) {
            destinations = tempData.destinations;
          }
        }

        const endTrip =
          "endTrip" in tempData &&
          typeof tempData.endTrip === "string" &&
          tempData.endTrip.trim() !== ""
            ? tempData.endTrip
            : null;
        const startDate =
          "startDate" in tempData &&
          typeof tempData.startDate === "string" &&
          tempData.startDate.trim() !== ""
            ? tempData.startDate
            : null;
        const endDate =
          "endDate" in tempData &&
          typeof tempData.endDate === "string" &&
          tempData.endDate.trim() !== ""
            ? tempData.endDate
            : null;

        return {
          startTrip,
          destinations,
          endTrip,
          startDate,
          endDate,
        };
      };

      const tempTravelDetails = getTempDataTravelDetails();

      // Get origin with comprehensive fallback
      let origin = "";
      if (
        travelDetailsData?.startTrip &&
        travelDetailsData.startTrip.trim() !== ""
      ) {
        origin = travelDetailsData.startTrip;
      } else if (tempTravelDetails?.startTrip) {
        origin = tempTravelDetails.startTrip;
      }

      // Get destinations with comprehensive fallback
      let destinations = "";
      if (
        travelDetailsData?.destination &&
        Array.isArray(travelDetailsData.destination) &&
        travelDetailsData.destination.length > 0
      ) {
        destinations = travelDetailsData.destination.join(", ");
      } else if (tempTravelDetails?.destinations) {
        if (typeof tempTravelDetails.destinations === "string") {
          destinations = tempTravelDetails.destinations;
        } else if (Array.isArray(tempTravelDetails.destinations)) {
          destinations = tempTravelDetails.destinations.join(", ");
        }
      }

      // Get end trip with comprehensive fallback
      let endTrip = "";
      if (
        travelDetailsData?.endTrip &&
        travelDetailsData.endTrip.trim() !== ""
      ) {
        endTrip = travelDetailsData.endTrip;
      } else if (tempTravelDetails?.endTrip) {
        endTrip = tempTravelDetails.endTrip;
      }

      // Get dates with comprehensive fallback
      let startDate = "";
      let endDate = "";

      if (
        travelDetailsData?.startDate &&
        travelDetailsData.startDate.trim() !== ""
      ) {
        startDate = travelDetailsData.startDate;
      } else if (tempTravelDetails?.startDate) {
        startDate = tempTravelDetails.startDate;
      }

      if (
        travelDetailsData?.endDate &&
        travelDetailsData.endDate.trim() !== ""
      ) {
        endDate = travelDetailsData.endDate;
      } else if (tempTravelDetails?.endDate) {
        endDate = tempTravelDetails.endDate;
      }

      // Calculate trip days only if both dates are available
      const tripDays =
        startDate && endDate ? calculateTripDays(startDate, endDate) : 0;

      // Build comprehensive itinerary with fallback logic
      let itinerary = "";

      // First try to use complete itinerary from travelDetailsData
      if (
        travelDetailsData?.completeItinerary &&
        travelDetailsData.completeItinerary.trim() !== ""
      ) {
        itinerary = travelDetailsData.completeItinerary;
      }
      // If no complete itinerary, build from tempData if available
      else if (tempTravelDetails?.startTrip && tempTravelDetails?.endTrip) {
        let destinationsStr = "";
        if (tempTravelDetails.destinations) {
          if (typeof tempTravelDetails.destinations === "string") {
            destinationsStr = tempTravelDetails.destinations;
          } else if (Array.isArray(tempTravelDetails.destinations)) {
            destinationsStr = tempTravelDetails.destinations.join(", ");
          }
        }

        itinerary = destinationsStr
          ? `${tempTravelDetails.startTrip} - ${destinationsStr} - ${tempTravelDetails.endTrip}`
          : `${tempTravelDetails.startTrip} - ${tempTravelDetails.endTrip}`;
      }
      // Build from processed data if available
      else if (origin && destinations) {
        itinerary =
          tripDays > 0
            ? `${origin} to ${destinations}, ${tripDays} days trip`
            : `${origin} to ${destinations}`;
      }

      // Return the travel information (validation will be done later)
      const result = {
        origin,
        destinations,
        endTrip,
        startDate,
        endDate,
        itinerary,
        tripDays,
      };

      return result;
    },
    [calculateTripDays],
  );

  // Helper function to build family members array for family transactions
  const buildFamilyMembers = useCallback(
    (data: ClientStepperData): FamilyMemberInfo[] => {
      const familyMembers: FamilyMemberInfo[] = [];
      // Add spouse if exists
      if (data.familyinformation?.spouse) {
        const spouse = data.familyinformation.spouse;
        familyMembers.push({
          title: spouse.title || "",
          firstName: spouse.firstName || "",
          middleName: spouse.middleName || "",
          lastName: spouse.lastName || "",
          suffix: spouse.suffix || "",
          birthDate: spouse.dateOfBirth || "",
          gender: spouse.gender || "",
          relationship: "Spouse",
          idType: spouse.idType || "",
          idNumber: spouse.idNumber || "",
          occupation: "", // Keep empty as spouse occupation is not captured in the form
          nationality: spouse.nationality || "", // FIXED: Use actual spouse nationality instead of hardcoded empty string
          address: {
            isSameAsPrincipal: spouse.shareAddressWithChild || true,
            ...(spouse.shareAddressWithChild
              ? {}
              : {
                  address1: spouse.houseUnitNo || "",
                  barangay: spouse.barangay || "-",
                  city: spouse.city || "",
                  province: spouse.province || "",
                  zipCode: spouse.zipCode || "",
                  country: spouse.country || "",
                }),
          },
        });
      }

      // Add children if exist
      if (data.familyinformation?.children) {
        data.familyinformation.children.forEach((child) => {
          familyMembers.push({
            title: child.title || "",
            firstName: child.firstName || "",
            middleName: child.middleName || "",
            lastName: child.lastName || "",
            suffix: child.suffix || "",
            birthDate: child.dateOfBirth || "",
            gender: child.gender || "",
            relationship: "Child", // Keep relationship logic but remove hardcoded defaults
            idType: "", // Remove hardcoded ID type
            idNumber: "", // Remove hardcoded ID number generation
            occupation: "", // Remove hardcoded occupation
            nationality: "", // Remove hardcoded nationality
            address: {
              isSameAsPrincipal: false, // Children might have different addresses
              address1: child.houseUnitNo || "",
              barangay: child.barangay || "-",
              city: child.city || "",
              province: child.province || "",
              zipCode: child.zipCode || "",
              country: child.country || "",
            },
          });
        });
      }

      return familyMembers;
    },
    [],
  );

  // Helper function to build principals array for group transactions
  const buildGroupPrincipals = useCallback(
    (
      data: ClientStepperData,
      selectedPlan: PlanOption | null,
      tempData?: TempData | null,
      contextGetPrincipalId?: () => number | null,
    ): GroupTransactionPrincipal[] => {
      const groupMembers = data.groupinformation?.groupMembers || [];
      const principals: GroupTransactionPrincipal[] = [];

      // Helper function to get principal_id with proper priority order
      const getPrincipalId = (): number => {
        // First priority: Use context's getPrincipalId function if available
        if (contextGetPrincipalId) {
          const contextId = contextGetPrincipalId();
          if (contextId && contextId > 0) {
            return contextId;
          }
        }

        // Second priority: Check tempData for principal_id
        if (
          tempData &&
          "principal_id" in tempData &&
          typeof tempData.principal_id === "number" &&
          tempData.principal_id > 0
        ) {
          return tempData.principal_id;
        }
        if (
          tempData &&
          "principalId" in tempData &&
          typeof tempData.principalId === "number" &&
          tempData.principalId > 0
        ) {
          return tempData.principalId;
        }

        return 0;
      };

      // CRITICAL FIX: Enhanced Group member email retrieval with comprehensive storage fallback mechanisms
      const getGroupMemberEmail = (member: GroupMember): string => {
        // PRIORITY 1: Use tempData email (from context)
        if (
          tempData &&
          "email" in tempData &&
          typeof tempData.email === "string" &&
          tempData.email.trim() !== ""
        ) {
          return tempData.email;
        }

        // PRIORITY 2: Get from sessionStorage email verification data (CORRECT KEY)
        try {
          const sessionEmailVerificationData = sessionStorage.getItem(
            "mts_session_emailVerificationData",
          );
          if (sessionEmailVerificationData) {
            const parsedData = JSON.parse(sessionEmailVerificationData);
            const sessionEmail = parsedData.email || parsedData.emailAddress;
            if (sessionEmail && sessionEmail.trim() !== "") {
              return sessionEmail;
            }
          }
        } catch (error) {
          console.warn(
            "useTransaction: Group error parsing sessionStorage mts_session_emailVerificationData:",
            error,
          );
        }

        // PRIORITY 3: Get from localStorage email verification data (FALLBACK KEY)
        try {
          const localEmailVerificationData = localStorage.getItem(
            "emailVerificationTempData",
          );
          if (localEmailVerificationData) {
            const parsedData = JSON.parse(localEmailVerificationData);
            const localEmail = parsedData.email || parsedData.emailAddress;
            if (localEmail && localEmail.trim() !== "") {
              return localEmail;
            }
          }
        } catch (error) {
          console.warn(
            "useTransaction: Group error parsing localStorage emailVerificationTempData:",
            error,
          );
        }

        // PRIORITY 4: Get from sessionStorage email verification data (FALLBACK KEY)
        try {
          const sessionEmailVerificationData = sessionStorage.getItem(
            "emailVerificationTempData",
          );
          if (sessionEmailVerificationData) {
            const parsedData = JSON.parse(sessionEmailVerificationData);
            const sessionEmail = parsedData.email || parsedData.emailAddress;
            if (sessionEmail && sessionEmail.trim() !== "") {
              return sessionEmail;
            }
          }
        } catch (error) {
          console.warn(
            "useTransaction: Group error parsing sessionStorage emailVerificationTempData:",
            error,
          );
        }

        // PRIORITY 5: Use principal data email (from email verification for existing users)
        if (
          principalData?.email &&
          typeof principalData.email === "string" &&
          principalData.email.trim() !== ""
        ) {
          return principalData.email;
        }

        // PRIORITY 6: Get from email backup
        try {
          const emailBackup = localStorage.getItem("planSelectionEmailBackup");
          if (emailBackup && emailBackup.trim() !== "") {
            return emailBackup;
          }
        } catch (error) {
          console.warn(
            "useTransaction: Group error getting email backup from localStorage:",
            error,
          );
        }

        // PRIORITY 7: Generate from member name (original behavior for new users)
        const generatedEmail = `${member.firstName?.toLowerCase() || "member"}.${member.lastName?.toLowerCase() || "doe"}@example.com`;
        return generatedEmail;
      };

      // Helper function to calculate contribution amount
      const calculateContributionAmount = (member: GroupMember): number => {
        // If member has explicit contribution amount, use it
        if (
          member.contribution?.amount &&
          typeof member.contribution.amount === "number"
        ) {
          return member.contribution.amount;
        }

        // Otherwise calculate based on premium amount
        let baseAmount = 1500; // Default fallback

        if (
          selectedPlan?.rate?.rateAmount &&
          selectedPlan.rate.rateAmount > 0
        ) {
          baseAmount = selectedPlan.rate.rateAmount;
        } else if (
          tempData?.rateAmount &&
          typeof tempData.rateAmount === "number" &&
          tempData.rateAmount > 0
        ) {
          baseAmount = tempData.rateAmount;
        }

        return baseAmount;
      };

      groupMembers.forEach((member, index) => {
        // Build address from member data
        const addressParts = [
          member.houseUnitNo,
          member.streetName,
          member.subdivisionVillage,
        ].filter((part) => part && part.trim() !== "");

        const address1 =
          addressParts.length > 0
            ? addressParts.join(" ")
            : `${index + 1}23 Group Street`;

        // Build beneficiaries array
        const beneficiaries = (member.beneficiaries || []).map(
          (beneficiary) => ({
            title: beneficiary.title || "",
            firstName: beneficiary.firstName || "",
            middleName: beneficiary.middleName || "",
            lastName: beneficiary.lastName || "",
            suffix: beneficiary.suffix || "",
            relationship: beneficiary.relationship || "",
          }),
        );

        // No default beneficiaries - let the form validation handle empty cases

        const principal: GroupTransactionPrincipal = {
          principal_id: getPrincipalId(),
          title: member.title || "",
          firstName: member.firstName || "",
          middleName: member.middleName || "",
          lastName: member.lastName || "",
          suffix: member.suffix || "",
          birthDate: member.dateOfBirth || "",
          gender: member.gender || "",
          occupation: member.occupation || "",
          nationality: member.nationality || "",
          civilStatus: member.civilStatus || "",
          idType: member.idType || "",
          idNumber: member.idNumber || "",
          address: {
            address1: address1,
            barangay: member.barangay || "-",
            city: member.city || "",
            province: member.province || "",
            zipCode: member.zipCode || "",
            country: member.country || "",
          },
          contact: {
            landlineCd: data.contactinformation?.landlineCountryCode || "+63",
            landlineAc: "2",
            landLineNumber: data.contactinformation?.landlinePhoneNumber || "",
            contactCd: data.contactinformation?.countryCode,
            contactNumber: data.contactinformation?.phoneNumber,
            email: getGroupMemberEmail(member),
          },
          beneficiaries: beneficiaries,
          contribution: {
            amount: calculateContributionAmount(member),
          },
        };

        principals.push(principal);
      });

      return principals;
    },
    [formatPhoneNumber],
  );

  // Helper function to create multi-trip transaction with correct structure
  const createMultiTripTransaction = useCallback(
    async (
      data: ClientStepperData,
      selectedPlan: PlanOption | null,
      travelDetailsData?: TravelDetailsData,
      _planSelection?: PlanSelection,
      tempData?: TempData | null,
      enhancedPlanData?: EnhancedPlanData | null,
      utmData?: UtmTrackingData | null,
    ): Promise<TransactionResponse> => {

      // Validate required data - Multi-Trip plans always need principalinsured
      if (!data.principalinsured) {
        throw new Error("Principal insured information is required");
      }

      if (!selectedPlan && !tempData?.productName) {
        throw new Error("Plan selection is required");
      }

      // Helper function to get principal_id from tempData
      const getPrincipalId = (): number => {
        if (
          tempData &&
          "principal_id" in tempData &&
          typeof tempData.principal_id === "number" &&
          tempData.principal_id > 0
        ) {
          return tempData.principal_id;
        }
        if (
          tempData &&
          "principalId" in tempData &&
          typeof tempData.principalId === "number" &&
          tempData.principalId > 0
        ) {
          return tempData.principalId;
        }

        return 0;
      };

      // Helper function to get birthDate with proper formatting
      const getBirthDate = (): string => {
        if (
          data.principalinsured?.dateOfBirth &&
          data.principalinsured.dateOfBirth.trim() !== ""
        ) {
          return data.principalinsured.dateOfBirth;
        }

        if (
          tempData &&
          "birthDate" in tempData &&
          typeof tempData.birthDate === "string" &&
          tempData.birthDate.trim() !== ""
        ) {
          return tempData.birthDate;
        }
        if (
          tempData &&
          "dateOfBirth" in tempData &&
          typeof tempData.dateOfBirth === "string" &&
          tempData.dateOfBirth.trim() !== ""
        ) {
          return tempData.dateOfBirth;
        }

        return "1990-01-15";
      };

      // CRITICAL FIX: Enhanced Multi-Trip email retrieval with comprehensive storage fallback mechanisms
      const getEmailFromData = (): string => {
        // PRIORITY 1: Use tempData email (from context)
        if (
          tempData &&
          "email" in tempData &&
          typeof tempData.email === "string" &&
          tempData.email.trim() !== ""
        ) {
          return tempData.email;
        }

        // PRIORITY 2: Get from sessionStorage email verification data (CORRECT KEY)
        try {
          const sessionEmailVerificationData = sessionStorage.getItem(
            "mts_session_emailVerificationData",
          );
          if (sessionEmailVerificationData) {
            const parsedData = JSON.parse(sessionEmailVerificationData);
            const sessionEmail = parsedData.email || parsedData.emailAddress;
            if (sessionEmail && sessionEmail.trim() !== "") {
              return sessionEmail;
            }
          }
        } catch (error) {
          console.warn(
            "useTransaction: Multi-Trip error parsing sessionStorage mts_session_emailVerificationData:",
            error,
          );
        }

        // PRIORITY 3: Get from localStorage email verification data (FALLBACK KEY)
        try {
          const localEmailVerificationData = localStorage.getItem(
            "emailVerificationTempData",
          );
          if (localEmailVerificationData) {
            const parsedData = JSON.parse(localEmailVerificationData);
            const localEmail = parsedData.email || parsedData.emailAddress;
            if (localEmail && localEmail.trim() !== "") {
              return localEmail;
            }
          }
        } catch (error) {
          console.warn(
            "useTransaction: Multi-Trip error parsing localStorage emailVerificationTempData:",
            error,
          );
        }

        // PRIORITY 4: Get from sessionStorage email verification data (FALLBACK KEY)
        try {
          const sessionEmailVerificationData = sessionStorage.getItem(
            "emailVerificationTempData",
          );
          if (sessionEmailVerificationData) {
            const parsedData = JSON.parse(sessionEmailVerificationData);
            const sessionEmail = parsedData.email || parsedData.emailAddress;
            if (sessionEmail && sessionEmail.trim() !== "") {
              return sessionEmail;
            }
          }
        } catch (error) {
          console.warn(
            "useTransaction: Multi-Trip error parsing sessionStorage emailVerificationTempData:",
            error,
          );
        }

        // PRIORITY 5: Use principal data email (from email verification for existing users)
        if (
          principalData?.email &&
          typeof principalData.email === "string" &&
          principalData.email.trim() !== ""
        ) {
          return principalData.email;
        }

        // PRIORITY 6: Use contact information email
        if (
          data.contactinformation &&
          "email" in data.contactinformation &&
          typeof data.contactinformation.email === "string" &&
          data.contactinformation.email.trim() !== ""
        ) {
          return data.contactinformation.email;
        }

        // PRIORITY 7: Get from email backup
        try {
          const emailBackup = localStorage.getItem("planSelectionEmailBackup");
          if (emailBackup && emailBackup.trim() !== "") {
            return emailBackup;
          }
        } catch (error) {
          console.warn(
            "useTransaction: Multi-Trip error getting email backup from localStorage:",
            error,
          );
        }

        console.warn(
          "useTransaction: Multi-Trip no email found in any fallback mechanism, using default",
        );
        return "john.doe@example.com";
      };

      // Helper function to build address from home address data
      const buildAddress = (): AddressInfo => {
        if (data.homeaddress) {
          const addressParts = [
            data.homeaddress.houseUnitNo,
            data.homeaddress.streetName,
            data.homeaddress.subdivisionVillage,
          ].filter((part) => part && part.trim() !== "");

          const address1 =
            addressParts.length > 0 ? addressParts.join(" ") : "";

          return {
            address1: address1,
            barangay: data.homeaddress.barangay || "-",
            city: data.homeaddress.city || "",
            province: data.homeaddress.province || "",
            zipCode: data.homeaddress.zipCode || "",
            country: data.homeaddress.country || "",
          };
        }

        return {
          address1: "",
          barangay: "",
          city: "",
          province: "",
          zipCode: "",
          country: "",
        };
      };

      // Build principal information
      const principal: PrincipalInfo = {
        principal_id: getPrincipalId(),
        title: data.principalinsured.title || "",
        firstName: data.principalinsured.firstName || "",
        middleName:
          tempData &&
          "middleName" in tempData &&
          typeof tempData.middleName === "string" &&
          tempData.middleName.trim() !== ""
            ? tempData.middleName
            : data.principalinsured.middleName || "",
        lastName: data.principalinsured.lastName || "",
        suffix:
          tempData &&
          "suffix" in tempData &&
          typeof tempData.suffix === "string" &&
          tempData.suffix.trim() !== ""
            ? tempData.suffix
            : data.principalinsured.suffix || "",
        birthDate: getBirthDate(),
        gender: data.principalinsured.gender || "",
        occupation: data.principalinsured.occupation || "",
        nationality: data.principalinsured.nationality || "",
        civilStatus: data.principalinsured.civilStatus || "",
        idType: data.principalinsured.idType || "",
        idNumber: data.principalinsured.idNumber || "",
        address: buildAddress(),
        contact: {
          landlineCd: data.contactinformation?.landlineCountryCode || "+63",
          landlineAc: "2",
          landLineNumber: data.contactinformation?.landlinePhoneNumber || "",
          contactCd: data.contactinformation?.countryCode,
          contactNumber: data.contactinformation?.phoneNumber,
          email: getEmailFromData(),
        },
      };

      // Helper function to get travel purpose from tempData
      const getTravelPurpose = (): string => {
        let travelPurpose = "";

        if (planSelectionTempData) {
          const parsedPlanData = JSON.parse(planSelectionTempData);
          travelPurpose =
            parsedPlanData?.travelFormData?.purpose ||
            parsedPlanData?.travelFormData?.purposeOfTravel ||
            "";
        }
        if (customerPortalSessionState && !travelPurpose) {
          const parsedCustomerPortalSessionState = JSON.parse(
            customerPortalSessionState,
          );
          travelPurpose =
            parsedCustomerPortalSessionState?.travelFormData?.purposeOfTravel ||
            "";
        }
        return travelPurpose;
      };

      // Helper function to get coverage start date
      const getCoverageStartDate = (): string => {
        // For Multi-Trip, use travelDate or current year
        if (
          travelDetailsData?.travelDate &&
          travelDetailsData.travelDate.trim() !== ""
        ) {
          // If travelDate is just a year, convert to date format
          const travelDate = travelDetailsData.travelDate;
          if (/^\d{4}$/.test(travelDate)) {
            return `${travelDate}-01-01`; // Start of the year
          }
          return travelDate;
        }

        // Fallback to tempData dates
        if (
          tempData &&
          "startDate" in tempData &&
          typeof tempData.startDate === "string" &&
          tempData.startDate.trim() !== ""
        ) {
          return tempData.startDate;
        }

        // Default to current year start
        return `${new Date().getFullYear()}-01-01`;
      };

      // Build Multi-Trip specific transaction details (simplified structure)
      const transactionDetails: MultiTripTransactionDetails = {
        numberOfTraveler: getNumberOfTravelers(data, "Multi Trip", tempData),
        travelPurpose: getTravelPurpose(),
        coverageStart: getCoverageStartDate(), // Only coverageStart for Multi-Trip
      };
      // Build product information
      const product: ProductInfo = {
        productId: selectedPlan?.productId || 4, // Default to 4 as shown in sample
      };

      // Build traveler details for Multi Trip
      const travelerDetails: TravelerDetails = {
        travelerTypeId: 4, // Multi Trip uses traveler type ID 4
      };

      // Build applicant information
      const applicant: ApplicantInfo = {
        title: data.applicantdetails?.title || principal.title,
        firstName: data.applicantdetails?.firstName || principal.firstName,
        middleName: data.applicantdetails?.middleName || "",
        lastName: data.applicantdetails?.lastName || principal.lastName,
        suffix: data.applicantdetails?.suffix || principal.suffix,
      };

      // Build beneficiaries information
      const beneficiaries: BeneficiaryInfo[] = [];

      if (
        data.beneficiarydetails?.beneficiaries &&
        data.beneficiarydetails.beneficiaries.length > 0
      ) {
        data.beneficiarydetails.beneficiaries.forEach((beneficiary) => {
          beneficiaries.push({
            title: beneficiary.title || "",
            firstName: beneficiary.firstName || "",
            middleName: beneficiary.middleName,
            lastName: beneficiary.lastName || "",
            suffix: beneficiary.suffix || "",
            relationship: beneficiary.relationship || "",
          });
        });
      }
      // Remove default beneficiaries - let form validation handle empty cases

      // Helper function to get currency ID from tempData
      const getCurrencyId = (): number => {
        if (
          tempData &&
          "currencyId" in tempData &&
          typeof tempData.currencyId === "number" &&
          tempData.currencyId > 0
        ) {
          return tempData.currencyId;
        }

        if (selectedPlan?.rate?.currency?.currencyId) {
          return selectedPlan.rate.currency.currencyId;
        }
        return 1;
      };

      // Helper function to extract numeric value from premium string
      const extractNumericValue = (premiumString: string): number => {
        if (!premiumString || typeof premiumString !== "string") return 0;

        const numericMatch = premiumString.replace(/[^\d.]/g, "");
        const numericValue = parseFloat(numericMatch);
        return isNaN(numericValue) ? 0 : numericValue;
      };

      // Helper function to get payment amount (original premium)
      const getPaymentAmount = (): number => {
        if (enhancedPlanData?.originalPremium) {
          const amount = extractNumericValue(enhancedPlanData.originalPremium);
          if (amount > 0) {
            return amount;
          }
        }

        if (
          tempData?.rateAmount &&
          typeof tempData.rateAmount === "number" &&
          tempData.rateAmount > 0
        ) {
          return tempData.rateAmount;
        }

        if (
          selectedPlan?.rate?.rateAmount &&
          selectedPlan.rate.rateAmount > 0
        ) {
          return selectedPlan.rate.rateAmount;
        }
        return 1500; // Default as shown in sample
      };

      // Helper function to get payment total amount (payment premium)
      const getPaymentTotalAmount = (paymentAmount: number): number => {
        if (enhancedPlanData?.paymentPremium) {
          const amount = extractNumericValue(enhancedPlanData.paymentPremium);
          if (amount > 0) {
            return amount;
          }
        }

        const totalAmount = Math.round(paymentAmount * 1.05); // 5% markup as shown in sample
        return totalAmount;
      };

      // Build payment information
      const currencyId = getCurrencyId();
      const paymentAmount = getPaymentAmount();
      const paymentTotalAmount = getPaymentTotalAmount(paymentAmount);

      const payment: PaymentInfo = {
        currencyId: currencyId,
        paymentAmount: paymentAmount,
        paymentTotalAmount: paymentTotalAmount,
      };


      const travelFormData: MultiTripTransactionDetails = transactionDetails;
      if (customerPortalSessionState) {
        const parsedCustomerPortalSessionState = JSON.parse(
          customerPortalSessionState,
        );
        const travelFormDataSessionState =
          parsedCustomerPortalSessionState?.travelFormData;
        travelFormData.coverageStart =
          travelFormDataSessionState?.travelDate ||
          travelFormDataSessionState?.startDate;
        travelFormData.travelPurpose =
          travelFormDataSessionState?.purposeOfTravel || "";
      }

      // Build Multi-Trip request (matches sample structure exactly)
      const requestBody: MultiTripTransactionRequest = {
        dataId: tempDataId,
        principal,
        transactionDetails: travelFormData,
        product,
        travelerDetails,
        applicant,
        beneficiaries,
        payment,
      };

      // Validate required fields before sending
      if (!principal.firstName || !principal.lastName || !principal.birthDate) {
        throw new Error("Principal insured information is incomplete");
      }

      // Multi-Trip validation
      if (
        !travelFormData.coverageStart ||
        travelFormData.coverageStart.trim() === ""
      ) {
        throw new Error("Coverage start date is required for Multi-Trip");
      }

      // Make API call to Multi-Trip endpoint
      const utmQsParams = buildUtmQsParams(utmData);

      const endpoint = "/api/transactions/multitrip" + utmQsParams;
      
      const responseData: TransactionResponse = await enhancedApi.post(
        endpoint,
        requestBody,
        {
          timeout: 30000,
          headers: {
            "Content-Type": "application/json",
          },
        },
        2,
      ); // High priority for transaction processing

      if (responseData.success && responseData.paymentLink) {
        return responseData;
      } else if (responseData.hasPendingError || responseData.hasOverlappingError) {
        return responseData;
      } else {
        throw new Error(
          responseData.message || "Failed to create multi-trip transaction",
        );
      }
    },
    [formatPhoneNumber, getNumberOfTravelers],
  );

  // Main function to create transaction
  const createTransaction = useCallback(
    async (
      data: ClientStepperData,
      selectedPlan: PlanOption | null,
      travelDetailsData?: TravelDetailsData,
      planSelection?: PlanSelection,
      tempData?: TempData | null,
      enhancedPlanData?: EnhancedPlanData | null,
      contextGetPrincipalId?: () => number | null,
      utmData?: UtmTrackingData | null,
    ): Promise<TransactionResponse> => {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setPaymentLink(null);

      try {
        if (!selectedPlan && !tempData?.productName) {
          throw new Error("Plan selection is required");
        }

        // Determine plan type
        const planType = determinePlanType(
          data,
          planSelection,
          travelDetailsData,
        );

        // Validate required data based on plan type
        if (planType === "Group") {
          // For Group Plans, validate group members instead of principalinsured
          if (
            !data.groupinformation?.groupMembers ||
            data.groupinformation.groupMembers.length === 0
          ) {
            throw new Error("Group member information is required");
          }
        } else {
          // For all other plan types (Individual, Family, Multi Trip), validate principalinsured
          if (!data.principalinsured) {
            throw new Error("Principal insured information is required");
          }
        }

        // Helper function to get principal_id from tempData
        const getPrincipalId = (): number => {
          if (
            tempData &&
            "principal_id" in tempData &&
            typeof tempData.principal_id === "number" &&
            tempData.principal_id > 0
          ) {
            return tempData.principal_id;
          }
          if (
            tempData &&
            "principalId" in tempData &&
            typeof tempData.principalId === "number" &&
            tempData.principalId > 0
          ) {
            return tempData.principalId;
          }

          return 0;
        };

        // Helper function to get birthDate with proper formatting
        const getBirthDate = (): string => {
          if (
            data.principalinsured?.dateOfBirth &&
            data.principalinsured.dateOfBirth.trim() !== ""
          ) {
            return data.principalinsured.dateOfBirth;
          }

          if (
            tempData &&
            "birthDate" in tempData &&
            typeof tempData.birthDate === "string" &&
            tempData.birthDate.trim() !== ""
          ) {
            return tempData.birthDate;
          }
          if (
            tempData &&
            "dateOfBirth" in tempData &&
            typeof tempData.dateOfBirth === "string" &&
            tempData.dateOfBirth.trim() !== ""
          ) {
            return tempData.dateOfBirth;
          }

          return "";
        };

        // CRITICAL FIX: Enhanced email retrieval with comprehensive storage fallback mechanisms
        const getEmailFromData = (): string => {
          // PRIORITY 1: Use tempData email (from context)
          if (
            tempData &&
            "email" in tempData &&
            typeof tempData.email === "string" &&
            tempData.email.trim() !== ""
          ) {
            return tempData.email;
          }

          // PRIORITY 2: Get from sessionStorage email verification data (CORRECT KEY)
          try {
            const sessionEmailVerificationData = sessionStorage.getItem(
              "mts_session_emailVerificationData",
            );
            if (sessionEmailVerificationData) {
              const parsedData = JSON.parse(sessionEmailVerificationData);
              const sessionEmail = parsedData.email || parsedData.emailAddress;
              if (sessionEmail && sessionEmail.trim() !== "") {
                return sessionEmail;
              }
            }
          } catch (error) {
            console.warn(
              "useTransaction: Error parsing sessionStorage mts_session_emailVerificationData:",
              error,
            );
          }

          // PRIORITY 3: Get from localStorage email verification data (FALLBACK KEY)
          try {
            const localEmailVerificationData = localStorage.getItem(
              "emailVerificationTempData",
            );
            if (localEmailVerificationData) {
              const parsedData = JSON.parse(localEmailVerificationData);
              const localEmail = parsedData.email || parsedData.emailAddress;
              if (localEmail && localEmail.trim() !== "") {
                return localEmail;
              }
            }
          } catch (error) {
            console.warn(
              "useTransaction: Error parsing localStorage emailVerificationTempData:",
              error,
            );
          }

          // PRIORITY 4: Get from sessionStorage email verification data (FALLBACK KEY)
          try {
            const sessionEmailVerificationData = sessionStorage.getItem(
              "emailVerificationTempData",
            );
            if (sessionEmailVerificationData) {
              const parsedData = JSON.parse(sessionEmailVerificationData);
              const sessionEmail = parsedData.email || parsedData.emailAddress;
              if (sessionEmail && sessionEmail.trim() !== "") {
                return sessionEmail;
              }
            }
          } catch (error) {
            console.warn(
              "useTransaction: Error parsing sessionStorage emailVerificationTempData:",
              error,
            );
          }

          // PRIORITY 5: Use principal data email (from email verification for existing users)
          if (
            principalData?.email &&
            typeof principalData.email === "string" &&
            principalData.email.trim() !== ""
          ) {
            return principalData.email;
          }

          // PRIORITY 6: Get from contact information step data
          if (
            data.contactinformation &&
            "email" in data.contactinformation &&
            typeof data.contactinformation.email === "string" &&
            data.contactinformation.email.trim() !== ""
          ) {
            return data.contactinformation.email;
          }

          // PRIORITY 7: Get from email backup
          try {
            const emailBackup = localStorage.getItem(
              "planSelectionEmailBackup",
            );
            if (emailBackup && emailBackup.trim() !== "") {
              return emailBackup;
            }
          } catch (error) {
            console.warn(
              "useTransaction: Error getting email backup from localStorage:",
              error,
            );
          }

          console.warn(
            "useTransaction: No email found in any fallback mechanism",
          );
          return "";
        };

        // Helper function to build address from home address data
        const buildAddress = (): AddressInfo => {
          if (data.homeaddress) {
            const addressParts = [
              data.homeaddress.houseUnitNo,
              data.homeaddress.streetName,
              data.homeaddress.subdivisionVillage,
            ].filter((part) => part && part.trim() !== "");

            const address1 =
              addressParts.length > 0 ? addressParts.join(" ") : "";

            return {
              address1: address1,
              barangay: data.homeaddress.barangay || "-",
              city: data.homeaddress.city || "",
              province: data.homeaddress.province || "",
              zipCode: data.homeaddress.zipCode || "",
              country: data.homeaddress.country || "",
            };
          }

          return {
            address1: "",
            barangay: "",
            city: "",
            province: "",
            zipCode: "",
            country: "",
          };
        };

        // Build principal information - only for non-Group plans
        let principal: PrincipalInfo | null = null;

        if (planType !== "Group") {
          // For Individual, Family, and Multi Trip plans, build principal from principalinsured data
          principal = {
            principal_id: getPrincipalId(),
            title: data.principalinsured!.title || "",
            firstName: data.principalinsured!.firstName || "",
            middleName:
              tempData &&
              "middleName" in tempData &&
              typeof tempData.middleName === "string" &&
              tempData.middleName.trim() !== ""
                ? tempData.middleName
                : data.principalinsured!.middleName || "",
            lastName: data.principalinsured!.lastName || "",
            suffix:
              tempData &&
              "suffix" in tempData &&
              typeof tempData.suffix === "string" &&
              tempData.suffix.trim() !== ""
                ? tempData.suffix
                : data.principalinsured!.suffix || "",
            birthDate: getBirthDate(),
            gender: data.principalinsured!.gender || "",
            occupation: data.principalinsured!.occupation || "",
            nationality: data.principalinsured!.nationality || "",
            civilStatus: data.principalinsured!.civilStatus || "",
            idType: data.principalinsured!.idType || "",
            idNumber: data.principalinsured!.idNumber || "",
            address: buildAddress(),
            contact: {
              landlineCd: data.contactinformation?.landlineCountryCode || "+63",
              landlineAc: "2",
              landLineNumber:
                data.contactinformation?.landlinePhoneNumber || "",
              contactCd: data.contactinformation?.countryCode,
              contactNumber: data.contactinformation?.phoneNumber,
              email: getEmailFromData(),
            },
          };
        }

        // Calculate number of travelers
        const numberOfTravelers = getNumberOfTravelers(
          data,
          planType,
          tempData,
        );

        // Get travel information
        const travelInfo = getTravelInformation(travelDetailsData, tempData);

        // Helper function to get travel purpose from tempData
        const getTravelPurpose = (): string => {
          let travelPurpose = "";

          if (planSelectionTempData) {
            const parsedPlanData = JSON.parse(planSelectionTempData);
            travelPurpose =
              parsedPlanData?.travelFormData?.purpose ||
              parsedPlanData?.travelFormData?.purposeOfTravel ||
              "";
          }
          if (customerPortalSessionState && !travelPurpose) {
            const parsedCustomerPortalSessionState = JSON.parse(
              customerPortalSessionState,
            );
            travelPurpose =
              parsedCustomerPortalSessionState?.travelFormData
                ?.purposeOfTravel || "";
          }
          return travelPurpose;
        };

        // Build transaction details
        const transactionDetails: TransactionDetails = {
          numberOfTraveler: numberOfTravelers,
          travelPurpose: getTravelPurpose(),
          origin: travelInfo.origin,
          destinations: travelInfo.destinations,
          itinerary: travelInfo.itinerary,
          coverageStart: travelInfo.startDate,
          coverageEnd: travelInfo.endDate,
          endTrip: travelInfo.endTrip,
        };

        // Build product information
        const product: ProductInfo = {
          productId: selectedPlan?.productId || 1,
        };

        // Build traveler details
        const travelerDetails: TravelerDetails = {
          travelerTypeId: getTravelerTypeId(planType),
        };

        // Build applicant information - will be updated for Group transactions later
        const applicant: ApplicantInfo = {
          title: data.applicantdetails?.title || principal?.title || "",
          firstName:
            data.applicantdetails?.firstName || principal?.firstName || "",
          middleName: data.applicantdetails?.middleName || "",
          lastName:
            data.applicantdetails?.lastName || principal?.lastName || "",
          suffix: data.applicantdetails?.suffix || principal?.suffix || "",
        };

        // Build beneficiaries information
        const beneficiaries: BeneficiaryInfo[] = [];

        if (
          data.beneficiarydetails?.beneficiaries &&
          data.beneficiarydetails.beneficiaries.length > 0
        ) {
          data.beneficiarydetails.beneficiaries.forEach((beneficiary) => {
            beneficiaries.push({
              title: beneficiary.title || "",
              firstName: beneficiary.firstName || "",
              middleName: beneficiary.middleName || "",
              lastName: beneficiary.lastName || "",
              suffix: beneficiary.suffix || "",
              relationship: beneficiary.relationship || "",
              address:
                planType === "Family"
                  ? {
                      address1: "",
                      barangay: "",
                      city: "",
                      province: "",
                      zipCode: "",
                      country: "",
                    }
                  : undefined,
            });
          });
        }
        // Remove default beneficiaries - let form validation handle empty cases

        // Helper function to get currency ID from tempData
        const getCurrencyId = (): number => {
          if (
            tempData &&
            "currencyId" in tempData &&
            typeof tempData.currencyId === "number" &&
            tempData.currencyId > 0
          ) {
            return tempData.currencyId;
          }

          if (selectedPlan?.rate?.currency?.currencyId) {
            return selectedPlan.rate.currency.currencyId;
          }

          return 1;
        };

        // Helper function to extract numeric value from premium string
        const extractNumericValue = (premiumString: string): number => {
          if (!premiumString || typeof premiumString !== "string") return 0;

          const numericMatch = premiumString.replace(/[^\d.]/g, "");
          const numericValue = parseFloat(numericMatch);
          return isNaN(numericValue) ? 0 : numericValue;
        };

        // Helper function to get payment amount (original premium)
        const getPaymentAmount = (): number => {
          if (enhancedPlanData?.originalPremium) {
            const amount = extractNumericValue(
              enhancedPlanData.originalPremium,
            );
            if (amount > 0) {
              return amount;
            }
          }

          if (
            tempData?.rateAmount &&
            typeof tempData.rateAmount === "number" &&
            tempData.rateAmount > 0
          ) {
            return tempData.rateAmount;
          }

          if (
            selectedPlan?.rate?.rateAmount &&
            selectedPlan.rate.rateAmount > 0
          ) {
            return selectedPlan.rate.rateAmount;
          }

          return planType === "Family" ? 3500 : 1500;
        };

        // Helper function to get payment total amount (payment premium)
        const getPaymentTotalAmount = (paymentAmount: number): number => {
          if (enhancedPlanData?.paymentPremium) {
            const amount = extractNumericValue(enhancedPlanData.paymentPremium);
            if (amount > 0) {
              return amount;
            }
          }

          const totalAmount = Math.round(paymentAmount * 1.05);
          return totalAmount;
        };

        // Build payment information
        const currencyId = getCurrencyId();
        const paymentAmount = getPaymentAmount();
        const paymentTotalAmount = getPaymentTotalAmount(paymentAmount);

        const payment: PaymentInfo = {
          currencyId: currencyId,
          paymentAmount: paymentAmount,
          paymentTotalAmount: paymentTotalAmount,
        };

        // Build base request - only for non-Group plans
        let baseRequest: BaseTransactionRequest | null = null;

        if (planType !== "Group") {
          baseRequest = {
            dataId: "",
            isForSomeoneElse: coverageType === CoverageType.SomeoneElse,
            principal: principal!,
            transactionDetails,
            product,
            travelerDetails,
            applicant,
            beneficiaries,
            payment,
          };
        }

        // Add shortSecure field only when planSelection.section is 'ShortSecure' and not Group plan
        if (planSelection?.section === "ShortSecure" && baseRequest) {
          const transportMode = travelDetailsData?.transportMode || "bus";
          baseRequest.shortSecure = {
            transportMode: transportMode,
          };
        }

        // Build complete request based on plan type
        let requestBody: TransactionRequest | GroupTransactionRequest;

        if (planType === "Group") {
          // For group transactions, use the new format with principals array
          const principals = buildGroupPrincipals(
            data,
            selectedPlan,
            tempData,
            contextGetPrincipalId,
          );

          // Update applicant to use first principal insured for group transactions
          const firstPrincipal = principals[0]; // This is the "principalRecord" equivalent
          const groupApplicant: ApplicantInfo = {
            title: data.applicantdetails?.title || firstPrincipal.title,
            firstName:
              data.applicantdetails?.firstName || firstPrincipal.firstName,
            middleName: data.applicantdetails?.middleName || "",
            lastName:
              data.applicantdetails?.lastName || firstPrincipal.lastName,
            suffix: data.applicantdetails?.suffix || firstPrincipal.suffix,
          };

          // Build priceAdjustments array (required for group transactions)
          const priceAdjustments = [
            {
              id: 2,
              type: "tax",
            },
          ];

          const groupRequest: GroupTransactionRequest = {
            dataId: tempDataId,
            transactionDetails,
            product,
            travelerDetails,
            applicant: groupApplicant,
            principals,
            payment,
            priceAdjustments,
          };

          // Add shortSecure field for group transactions if needed
          if (planSelection?.section === "ShortSecure") {
            const transportMode = travelDetailsData?.transportMode || "bus";
            groupRequest.shortSecure = {
              transportMode: transportMode,
            };
          }

          requestBody = groupRequest;
        } else if (planType === "Family") {
          // For family transactions, add family members array
          const familyMembers = buildFamilyMembers(data);
          requestBody = {
            ...baseRequest!,
            family: familyMembers,
          } as FamilyTransactionRequest;

        } else if (planType === "Multi Trip") {
          // For Multi Trip, use the specialized function
          return await createMultiTripTransaction(
            data,
            selectedPlan,
            travelDetailsData,
            planSelection,
            tempData,
            enhancedPlanData,
            utmData
          );
        } else {
          // For individual transactions
          requestBody = baseRequest! as IndividualTransactionRequest;
        }

        // Enhanced validation for required fields
        const validationErrors = [];

        // Validate principal information for non-Group plans
        if (planType !== "Group" && principal) {
          if (!principal.firstName || principal.firstName.trim() === "") {
            validationErrors.push("Principal first name is required");
          }
          if (!principal.lastName || principal.lastName.trim() === "") {
            validationErrors.push("Principal last name is required");
          }
          if (!principal.birthDate || principal.birthDate.trim() === "") {
            validationErrors.push("Principal birth date is required");
          }
          // Only validate email and contact if they're truly required by the API
          // Remove strict validation to maintain functionality
        }

        // Validate group members for Group plans
        if (planType === "Group") {
          const groupMembers = data.groupinformation?.groupMembers || [];
          if (groupMembers.length === 0) {
            validationErrors.push("At least one group member is required");
          } else {
            groupMembers.forEach((member, index) => {
              if (!member.firstName || member.firstName.trim() === "") {
                validationErrors.push(
                  `Group member ${index + 1} first name is required`,
                );
              }
              if (!member.lastName || member.lastName.trim() === "") {
                validationErrors.push(
                  `Group member ${index + 1} last name is required`,
                );
              }
            });
          }
        }

        // Enhanced travel information validation - only validate if data is expected

        // Validate travel information for non-Multi-Trip plans
        // Only validate travel info if tempData or travelDetailsData suggests it should be present
        const shouldValidateTravel = tempData || travelDetailsData;
        if (shouldValidateTravel) {
          if (
            !transactionDetails.coverageStart ||
            transactionDetails.coverageStart.trim() === ""
          ) {
            validationErrors.push("Coverage start date is required");
          }
          if (
            !transactionDetails.coverageEnd ||
            transactionDetails.coverageEnd.trim() === ""
          ) {
            validationErrors.push("Coverage end date is required");
          }
        }

        // Throw validation error if any critical issues found
        if (validationErrors.length > 0) {
          console.error("useTransaction: Validation failed:", validationErrors);
          console.error("useTransaction: Current data:", {
            planType,
            principal: principal
              ? {
                  firstName: principal.firstName,
                  lastName: principal.lastName,
                  birthDate: principal.birthDate,
                  email: principal.contact.email,
                  contactNumber: principal.contact.contactNumber,
                }
              : null,
            transactionDetails,
            travelDetailsData,
            tempData,
          });
          throw new Error(`Validation failed: ${validationErrors.join(", ")}`);
        }
        // Get the appropriate endpoint for the plan type
        const endpoint = getTransactionEndpoint(planType, utmData);
        requestBody.dataId = tempDataId;
        // Make API call
        const responseData: TransactionResponse = await enhancedApi.post(
          endpoint,
          requestBody,
          {
            timeout: 30000,
            headers: {
              "Content-Type": "application/json",
            },
          },
          2,
        ); // High priority for transaction processing

        if (responseData.success && responseData.paymentLink) {
          setSuccess(true);
          setPaymentLink(responseData.paymentLink);
          return responseData;
        } else if (responseData.hasPendingError || responseData.hasOverlappingError) {
          return responseData;
        } else {
          throw new Error(
            responseData.message ||
              `Failed to create ${planType.toLowerCase()} transaction`,
          );
        }
      } catch (err) {

        let errorMessage = `Failed to create ${determinePlanType(data, planSelection, travelDetailsData).toLowerCase()} transaction`;

        if (axios.isAxiosError(err)) {
          if (err.response) {
            const serverMessage =
              err.response.data?.message || err.response.statusText;
            errorMessage = `Server error (${err.response.status}): ${serverMessage}`;
          } else if (err.request) {
            errorMessage =
              "Network error: Unable to connect to server. Please check your internet connection.";
          } else {
            errorMessage = `Request error: ${err.message}`;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }

        setError(errorMessage);

        return {
          success: false,
          code:
            axios.isAxiosError(err) && err.response?.status
              ? err.response.status
              : 500,
          paymentLink: "",
          message: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    [
      determinePlanType,
      getTransactionEndpoint,
      getTravelerTypeId,
      formatPhoneNumber,
      getEmailAddress,
      getNumberOfTravelers,
      getTravelInformation,
      buildFamilyMembers,
      buildGroupPrincipals,
      createMultiTripTransaction,
    ],
  );

  // Clear all state
  const clearState = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
    setPaymentLink(null);
  }, []);

  return {
    createTransaction,
    loading,
    error,
    success,
    paymentLink,
    clearState,
  };
};

// Export the hook with backward compatibility alias
export const useIndividualTransaction = useTransaction;
